import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsOrdersByLocation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const periodMode = this.props.periodMode || 'last_30d';

    let queryData = {
      periodMode: periodMode,
    };

    let data = BackofficeReportsApi.getPromotionsOrdersByLocation(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || data || [],
      });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const weightedDiscountRate = totalSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalSalesFromClaims) * 100
      : 0;

    const averageDiscountPerOrder = totalOrdersWithClaims > 0
      ? totalDiscountFromClaims / totalOrdersWithClaims
      : 0;

    const highCostLocations = rows.filter(
      (row) => Number(row.discount_rate || 0) > 15
    ).length;

    const topLocation = [...rows].sort(
      (a, b) => Number(b.orders_with_claims || 0) - Number(a.orders_with_claims || 0)
    )[0];

    const headers = [
      { key: 'location_name', label: 'Location' },
      { key: 'orders_with_claims', label: 'Orders' },
      { key: 'orders_share', label: 'Share %' },
      { key: 'sales_from_claims', label: 'Order value' },
      { key: 'discount_from_claims', label: 'Discount' },
      { key: 'discount_rate', label: 'Cost %' },
      { key: 'average_discount', label: 'Avg. discount' },
    ];

    const csvRows = [
      ['Promo orders by location'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total promo orders', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total order value', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted cost %', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per promo order', averageDiscountPerOrder.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Locations with cost % over 15%', highCostLocations].map((value) => this.csvValue(value)).join(','),
      ['Top location by promo orders', topLocation ? topLocation.location_name || 'Unknown' : ''].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'orders_share' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `promo_orders_by_location_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Promo orders by location
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.25,
                  }}
                >
                  Where promotions generate orders, sales and discount cost
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 720, md: 780 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Share</TableCell>
                    <TableCell align="right">Order value</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Avg.</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => {
                    const ordersShare = Number(row.orders_share || 0);
                    const discountRate = Number(row.discount_rate || 0);

                    return (
                      <TableRow key={`${row.location_iref || 'loc'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.location_name || 'Unknown'}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.orders_with_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          <Stack spacing={0.45}>
                            <Typography
                              sx={{
                                fontSize: 'inherit',
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {formatPercent(ordersShare)}
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={Math.min(ordersShare, 100)}
                              sx={{
                                height: 5,
                                borderRadius: 999,
                                bgcolor: 'rgba(15, 23, 42, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  bgcolor: '#1a237e',
                                },
                              }}
                            />
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.sales_from_claims)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.discount_from_claims)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: discountRate > 15 ? '#b91c1c' : '#047857',
                          }}
                        >
                          {formatPercent(discountRate)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.average_discount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No promotion location data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsOrdersByLocation;
